/* ==========================================================================
   AURUM ENGINE — moteur d'animations « Tiitoune Aurum »

   IIFE vanilla, zéro dépendance. Respecte le contrat de aurum.css :
   • lit <body data-anim-level="spectaculaire|equilibre|off"> (theme.liquid) ;
   • pose body.anim-off quand les animations sont coupées (LEVEL 0) ;
   • pose html.no-vt quand LEVEL < 2 (coupe les View Transitions, section 11
     de aurum.css) — theme.liquid pose déjà no-vt en synchrone dans <head>,
     cette ligne sert de filet de secours ;
   • ajoute .is-visible sur [data-reveal] (les transitions sont en CSS) ;
   • pose will-change inline sur [data-tilt]/[data-parallax] pendant
     l'animation et le retire au repos (le moteur en est l'unique
     propriétaire : aucune règle will-change dans aurum.css).

   Niveaux :
     LEVEL 0 — tout coupé (reduced motion ou réglage « off ») ;
     LEVEL 1 — allégé (mobile, économiseur de données ou « équilibré ») ;
     LEVEL 2 — spectaculaire (tout actif).

   API publique : window.AurumEngine.refresh(root) — re-scanne [data-reveal]
   et [data-counter] dans un sous-arbre injecté (drawer panier, facettes…).
   ========================================================================== */
(function () {
  'use strict';

  var body = document.body;
  var docEl = document.documentElement;

  /* ------------------------------------------------------------------
     Calcul du niveau d'animation
     ------------------------------------------------------------------ */
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var attr = body.dataset.animLevel || 'spectaculaire';
  var mobile = matchMedia('(max-width: 749px)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData === true);
  var LEVEL = (reduced || attr === 'off') ? 0 : (mobile || saveData || attr === 'equilibre') ? 1 : 2;

  var POINTER_FINE = matchMedia('(pointer: fine)').matches;
  var HAS_IO = 'IntersectionObserver' in window;
  var GOLD_RGB = '200, 169, 83';

  /* View Transitions : approche CSS-first (@view-transition dans aurum.css).
     Sous le niveau « spectaculaire », la classe no-vt neutralise le fondu.
     (Déjà posée en synchrone par theme.liquid ; doublon volontaire.) */
  if (LEVEL < 2) docEl.classList.add('no-vt');

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* Collecte dans un sous-arbre : inclut la racine elle-même si elle matche
     (cas shopify:section:load où event.target est la section) */
  function collect(root, sel) {
    var list = qsa(sel, root);
    if (root !== document && root.matches && root.matches(sel)) list.unshift(root);
    return list;
  }

  function clamp(v, min, max) {
    return Math.min(Math.max(v, min), max);
  }

  /* ==================================================================
     Module 1 — Révélations au scroll ([data-reveal] et variantes
     curtain/draw, toutes pilotées par le CSS : on ne fait qu'ajouter
     .is-visible puis cesser d'observer).
     ================================================================== */
  var revealIO = null;
  var counterIO = null;

  if (LEVEL > 0 && HAS_IO) {
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealIO.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
  }

  function observeAll(root) {
    root = root || document;
    collect(root, '[data-reveal]').forEach(function (el) {
      if (el.classList.contains('is-visible')) return;
      /* LEVEL 0 (ou navigateur sans IntersectionObserver) :
         visible immédiatement */
      if (revealIO) revealIO.observe(el); else el.classList.add('is-visible');
    });
    collect(root, '[data-counter]').forEach(observeCounter);
  }

  /* ==================================================================
     Module 5 — Compteurs ([data-counter="128"]).
     L'élément doit contenir UNIQUEMENT le nombre : on réécrit tout son
     textContent. Les libellés (préfixe « + », suffixe « clientes »…)
     sont à placer autour, dans des éléments voisins, par les sections.
     ================================================================== */
  if (LEVEL > 0 && HAS_IO) {
    counterIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        counterIO.unobserve(entry.target);
        startCounter(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px' });
  }

  /* Tolère les espaces (y compris insécables et fines) dans data-counter,
     ex. data-counter="1 200" ; NaN → le compteur est ignoré */
  function counterTarget(el) {
    return parseFloat(String(el.dataset.counter).replace(/[\s  ]/g, ''));
  }

  /* Rendu localisé : 1200 → « 1 200 » */
  function formatCounter(v) {
    return Math.round(v).toLocaleString('fr-FR');
  }

  function observeCounter(el) {
    if (el.dataset.counterDone) return;
    if (counterIO) { counterIO.observe(el); return; }
    /* Sans animation : valeur finale directe */
    var target = counterTarget(el);
    if (isFinite(target)) {
      el.dataset.counterDone = '1';
      el.textContent = formatCounter(target);
    }
  }

  function startCounter(el) {
    var target = counterTarget(el);
    if (!isFinite(target) || el.dataset.counterDone) return;
    el.dataset.counterDone = '1';
    var DURATION = 1200;
    var t0 = 0;
    function frame(now) {
      if (!t0) t0 = now;
      var t = clamp((now - t0) / DURATION, 0, 1);
      var eased = 1 - Math.pow(1 - t, 3); /* easeOutCubic */
      el.textContent = formatCounter(target * eased);
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------------
     Filet de sécurité bas de page : avec le rootMargin négatif, les
     éléments collés au bas du document peuvent ne jamais croiser la
     zone d'observation. Arrivé au bout de la page (ou si la page est
     plus courte que le viewport au chargement), on révèle tout ce qui
     reste et on lance les compteurs en attente — idempotent grâce à
     .is-visible et data-counter-done.
     ------------------------------------------------------------------ */
  function forceRemaining() {
    qsa('[data-reveal]').forEach(function (el) {
      if (el.classList.contains('is-visible')) return;
      el.classList.add('is-visible');
      if (revealIO) revealIO.unobserve(el);
    });
    qsa('[data-counter]').forEach(function (el) {
      if (el.dataset.counterDone) return;
      if (counterIO) counterIO.unobserve(el);
      startCounter(el);
    });
  }

  function bottomCheck() {
    if (window.scrollY + window.innerHeight >= docEl.scrollHeight - 2) forceRemaining();
  }

  if (LEVEL > 0 && HAS_IO) {
    window.addEventListener('scroll', bottomCheck, { passive: true });
  }

  /* ==================================================================
     Module 2 — Particules dorées ([data-particles="hero|section"]).
     Un canvas par conteneur, mais UNE SEULE boucle rAF partagée ;
     pause quand l'onglet est caché ou quand le conteneur sort de
     l'écran ; ~30 fps en LEVEL 1 (une frame sur deux). Étoiles
     4 branches et points, dérive lente vers le haut avec un léger
     balancement horizontal. Les systèmes dont le conteneur a quitté
     le DOM (rechargement de section) sont purgés.
     ================================================================== */
  var particleSystems = [];
  var particleRaf = 0;
  var particleSkip = false; /* LEVEL 1 : bascule une frame sur deux */
  var particleDpr = Math.min(window.devicePixelRatio || 1, 2);
  var particleVisIO = null;
  var particleSizeRO = null;

  function findParticleSystem(el) {
    for (var i = 0; i < particleSystems.length; i++) {
      if (particleSystems[i].el === el) return particleSystems[i];
    }
    return null;
  }

  function makeParticle(w, h) {
    var star = Math.random() < 0.18; /* étoile occasionnelle */
    var alpha = 0.2 + Math.random() * 0.55;
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      size: star ? 3 + Math.random() * 5 : 1 + Math.random() * 2,
      /* alpha constant par particule : fillStyle précalculé une fois */
      fill: 'rgba(' + GOLD_RGB + ', ' + alpha.toFixed(3) + ')',
      speed: 0.15 + Math.random() * 0.35,
      swayAmp: 8 + Math.random() * 18,
      swayFreq: 0.0004 + Math.random() * 0.0008,
      phase: Math.random() * Math.PI * 2,
      star: star
    };
  }

  /* Étoile 4 branches (8 sommets alternés extérieur/intérieur) */
  function drawStar(ctx, x, y, r) {
    var inner = r * 0.4;
    ctx.beginPath();
    for (var i = 0; i < 8; i++) {
      var ang = (Math.PI / 4) * i - Math.PI / 2;
      var rad = (i % 2 === 0) ? r : inner;
      var px = x + Math.cos(ang) * rad;
      var py = y + Math.sin(ang) * rad;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  }

  /* Purge des systèmes dont le conteneur n'est plus dans le DOM
     (section rechargée dans l'éditeur de thème) */
  function pruneParticles() {
    for (var i = particleSystems.length - 1; i >= 0; i--) {
      var sys = particleSystems[i];
      if (sys.el.isConnected) continue;
      if (particleVisIO) particleVisIO.unobserve(sys.el);
      if (particleSizeRO) particleSizeRO.unobserve(sys.el);
      particleSystems.splice(i, 1);
    }
  }

  function particleStep(now) {
    particleRaf = 0;
    pruneParticles();
    var anyVisible = particleSystems.some(function (sys) {
      return sys.visible && sys.w > 0;
    });
    /* LEVEL 1 : on saute une frame sur deux (~30 fps) */
    if (LEVEL === 1) {
      particleSkip = !particleSkip;
      if (particleSkip) {
        if (anyVisible && !document.hidden) particleRaf = requestAnimationFrame(particleStep);
        return;
      }
    }
    particleSystems.forEach(function (sys) {
      if (!sys.visible || !sys.w) return;
      var ctx = sys.ctx;
      ctx.clearRect(0, 0, sys.w, sys.h);
      sys.parts.forEach(function (p) {
        p.y -= p.speed;
        if (p.y < -10) { p.y = sys.h + 10; p.x = Math.random() * sys.w; }
        var x = p.x + Math.sin(now * p.swayFreq + p.phase) * p.swayAmp;
        ctx.fillStyle = p.fill;
        if (p.star) {
          drawStar(ctx, x, p.y, p.size);
        } else {
          ctx.beginPath();
          ctx.arc(x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    });
    if (anyVisible && !document.hidden) particleRaf = requestAnimationFrame(particleStep);
  }

  function wakeParticles() {
    if (!particleRaf && !document.hidden) particleRaf = requestAnimationFrame(particleStep);
  }

  function initParticles(root) {
    if (LEVEL < 2 || !HAS_IO || !('ResizeObserver' in window)) return;
    var containers = collect(root || document, '[data-particles]').filter(function (el) {
      return !el.dataset.aurumParticles; /* déjà initialisé */
    });
    if (!containers.length) return;

    /* Observateurs et boucle partagés, créés au premier appel */
    if (!particleVisIO) {
      particleVisIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var sys = findParticleSystem(entry.target);
          if (sys) sys.visible = entry.isIntersecting;
        });
        wakeParticles();
      });

      particleSizeRO = new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          var sys = findParticleSystem(entry.target);
          if (!sys) return;
          /* contentRect : pas de getBoundingClientRect forcé ici */
          sys.w = Math.max(1, Math.round(entry.contentRect.width));
          sys.h = Math.max(1, Math.round(entry.contentRect.height));
          sys.canvas.width = sys.w * particleDpr;
          sys.canvas.height = sys.h * particleDpr;
          sys.ctx.setTransform(particleDpr, 0, 0, particleDpr, 0, 0);
          /* Semis différé : on attend une vraie largeur (conteneur
             réellement affiché, pas un drawer encore fermé) */
          if (!sys.parts.length && sys.w > 1) {
            for (var n = 0; n < sys.count; n++) sys.parts.push(makeParticle(sys.w, sys.h));
          }
        });
        wakeParticles();
      });

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          if (particleRaf) { cancelAnimationFrame(particleRaf); particleRaf = 0; }
        } else {
          wakeParticles();
        }
      });
    }

    containers.forEach(function (container) {
      container.dataset.aurumParticles = '1';
      var isHero = container.getAttribute('data-particles') === 'hero';
      var count = isHero ? (LEVEL === 2 ? 64 : 20) : (LEVEL === 2 ? 26 : 10);

      /* Le canvas se place en absolu derrière le contenu :
         le conteneur doit être positionné.
         isolation:isolate crée un contexte d'empilement local ;
         le canvas en z-index:-1 passe sous tout le contenu du conteneur. */
      if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }
      container.style.isolation = 'isolate';
      var canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:-1;';
      container.prepend(canvas);

      particleSystems.push({
        el: container,
        canvas: canvas,
        ctx: canvas.getContext('2d'),
        count: count,
        parts: [],
        w: 0,
        h: 0,
        visible: false
      });
      particleVisIO.observe(container);
      particleSizeRO.observe(container);
    });
  }

  /* ==================================================================
     Module 3 — Tilt 3D ([data-tilt]) — LEVEL 2 + pointeur fin.
     rotateX/Y max ±7° autour du centre + scale(1.02), perspective 800px.
     will-change posé au survol, retiré après le retour. Le rect est mis
     en cache au survol et invalidé au premier scroll (one-shot).
     ================================================================== */
  function initTilt(root) {
    collect(root || document, '[data-tilt]').forEach(function (el) {
      if (el.dataset.aurumTilt) return; /* déjà initialisé */
      /* Combinaison interdite — transform partagé avec la parallaxe */
      if (el.hasAttribute('data-parallax')) return;
      el.dataset.aurumTilt = '1';

      var raf = 0;
      var px = 0;
      var py = 0;
      var hover = false;
      /* Vrai dès qu'un transform a réellement été écrit pendant ce
         survol ; remis à false à chaque pointerenter. */
      var applied = false;
      /* Cache du getBoundingClientRect, invalidé au scroll */
      var rect = null;

      function invalidateRect() {
        rect = null;
      }

      function update() {
        raf = 0;
        if (!hover) return;
        if (!rect) {
          rect = el.getBoundingClientRect();
          /* One-shot : un scroll suffit à invalider le cache ; le
             ré-armement est dédupliqué par addEventListener tant que
             l'écouteur précédent n'a pas été consommé */
          window.addEventListener('scroll', invalidateRect, { passive: true, once: true });
        }
        if (!rect.width || !rect.height) { rect = null; return; }
        var dx = clamp((px - rect.left) / rect.width - 0.5, -0.5, 0.5);
        var dy = clamp((py - rect.top) / rect.height - 0.5, -0.5, 0.5);
        var ry = (dx * 14).toFixed(2);  /* ±7° */
        var rx = (dy * -14).toFixed(2); /* ±7° */
        el.style.transform = 'perspective(800px) rotateX(' + rx + 'deg) rotateY(' + ry + 'deg) scale(1.02)';
        applied = true;
      }

      el.addEventListener('pointerenter', function () {
        hover = true;
        applied = false;
        rect = null;
        el.style.willChange = 'transform';
        /* Légère transition de lissage entre les frames rAF */
        el.style.transition = 'transform 0.2s ease-out';
      });

      el.addEventListener('pointermove', function (event) {
        px = event.clientX;
        py = event.clientY;
        /* Une seule mise à jour programmée par frame */
        if (!raf) raf = requestAnimationFrame(update);
      }, { passive: true });

      el.addEventListener('pointerleave', function () {
        hover = false;
        if (!applied) {
          /* Aucun transform écrit : pas de transition de retour à
             attendre — on libère will-change immédiatement. */
          el.style.willChange = '';
          el.style.transition = '';
          return;
        }
        el.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
        el.style.transform = '';
      });

      /* Retour terminé (ou annulé) : on libère will-change et la
         transition inline. On s'assure que l'événement concerne bien la
         propriété transform pour éviter tout faux déclenchement. */
      function onTransitionDone(event) {
        if (event.target === el && event.propertyName === 'transform' && !hover) {
          el.style.willChange = '';
          el.style.transition = '';
        }
      }
      el.addEventListener('transitionend', onTransitionDone);
      el.addEventListener('transitioncancel', onTransitionDone);
    });
  }

  /* ==================================================================
     Module 4 — Parallaxe ([data-parallax="0.2"]) — LEVEL >= 1.
     Décalage vertical proportionnel à la distance au centre du viewport,
     appliqué via style.transform — la propriété CSS translate est
     réservée au système de révélation. En conséquence :
     • ne PAS combiner data-parallax et data-reveal sur le même élément
       (envelopper l'un dans l'autre si besoin) ;
     • ne pas combiner data-parallax et data-tilt non plus.
     Mises à jour rAF, uniquement quand l'élément est proche du viewport.
     Les éléments retirés du DOM sont purgés.
     ================================================================== */
  var parallaxItems = [];
  var parallaxRaf = 0;
  var parallaxIO = null;
  var parallaxBound = false;

  function pruneParallax() {
    for (var i = parallaxItems.length - 1; i >= 0; i--) {
      if (parallaxItems[i].el.isConnected) continue;
      if (parallaxIO) parallaxIO.unobserve(parallaxItems[i].el);
      parallaxItems.splice(i, 1);
    }
  }

  function parallaxUpdate() {
    parallaxRaf = 0;
    pruneParallax();
    var viewportCenter = window.innerHeight / 2;
    parallaxItems.forEach(function (item) {
      if (!item.active) return;
      var rect = item.el.getBoundingClientRect();
      /* Centre « naturel » : on retranche le décalage déjà appliqué
         pour éviter toute boucle de rétroaction */
      var center = rect.top + rect.height / 2 - item.ty;
      var ty = (center - viewportCenter) * -item.factor;
      if (Math.abs(ty - item.ty) < 0.5) return;
      item.ty = ty;
      item.el.style.transform = 'translateY(' + ty.toFixed(1) + 'px)';
    });
  }

  function scheduleParallax() {
    if (!parallaxRaf) parallaxRaf = requestAnimationFrame(parallaxUpdate);
  }

  function initParallax(root) {
    if (!HAS_IO) return;
    var els = collect(root || document, '[data-parallax]').filter(function (el) {
      if (el.dataset.aurumParallax) return false; /* déjà initialisé */
      /* Combinaison interdite — transform partagé avec le tilt */
      if (el.hasAttribute('data-tilt')) return false;
      return true;
    });
    if (!els.length) return;

    /* Observateur partagé, créé au premier appel */
    if (!parallaxIO) {
      parallaxIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          for (var i = 0; i < parallaxItems.length; i++) {
            if (parallaxItems[i].el !== entry.target) continue;
            parallaxItems[i].active = entry.isIntersecting;
            /* will-change posé à l'approche du viewport, retiré à la
               sortie — le moteur en est l'unique propriétaire */
            parallaxItems[i].el.style.willChange = entry.isIntersecting ? 'transform' : '';
            break;
          }
        });
        scheduleParallax();
      }, { rootMargin: '20% 0px 20% 0px' });
    }

    els.forEach(function (el) {
      el.dataset.aurumParallax = '1';
      var factor = parseFloat(el.dataset.parallax);
      if (!isFinite(factor)) factor = 0.2;
      parallaxItems.push({ el: el, factor: clamp(factor, 0, 0.5), ty: 0, active: false });
      parallaxIO.observe(el);
    });

    if (!parallaxBound) {
      parallaxBound = true;
      window.addEventListener('scroll', scheduleParallax, { passive: true });
      window.addEventListener('resize', scheduleParallax, { passive: true });
    }
    scheduleParallax();
  }

  /* ==================================================================
     Module 7 — Traînée de curseur dorée — LEVEL 2 + pointeur fin
     + réglage data-anim-cursor="true" sur <body>.
     Additive : le curseur natif reste affiché (pas de cursor:none).
     Chaîne de ~12 points qui suivent le pointeur avec amorti ; la
     traînée s'estompe à l'arrêt (donc aussi au-dessus des iframes,
     qui n'émettent pas de pointermove vers le document parent) et la
     boucle s'arrête quand l'onglet est caché (canvas nettoyé pour ne
     pas laisser de traînée figée).
     ================================================================== */
  function initCursorTrail() {
    var canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999;';
    body.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var COUNT = 12;
    var dots = [];
    for (var i = 0; i < COUNT; i++) dots.push({ x: -100, y: -100 });
    var mx = -100;
    var my = -100;
    var raf = 0;
    var lastMove = 0;
    var fade = 0;

    function resize() {
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    function step(now) {
      raf = 0;
      /* Visible tant que le pointeur bouge, estompée à l'arrêt */
      var idle = now - lastMove;
      fade = idle < 120 ? Math.min(1, fade + 0.15) : Math.max(0, fade - 0.06);

      var tx = mx;
      var ty = my;
      dots.forEach(function (dot) {
        dot.x += (tx - dot.x) * 0.35;
        dot.y += (ty - dot.y) * 0.35;
        tx = dot.x;
        ty = dot.y;
      });

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      if (fade > 0.01) {
        for (var d = 0; d < COUNT; d++) {
          var strength = 1 - d / COUNT;
          ctx.fillStyle = 'rgba(' + GOLD_RGB + ', ' + (0.45 * strength * fade).toFixed(3) + ')';
          ctx.beginPath();
          ctx.arc(dots[d].x, dots[d].y, 1 + 2.5 * strength, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      if (fade > 0 && !document.hidden) raf = requestAnimationFrame(step);
    }

    window.addEventListener('pointermove', function (event) {
      mx = event.clientX;
      my = event.clientY;
      lastMove = performance.now();
      if (!raf && !document.hidden) raf = requestAnimationFrame(step);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
        /* Pas de traînée figée pendant que l'onglet est caché ; au
           retour, le prochain pointermove relance la boucle */
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      }
    });
  }

  /* ==================================================================
     Amorçage — chaque module est gardé par son niveau et isolé dans
     son propre try/catch.
     ================================================================== */

  /* API publique : permet aux rendus injectés (drawer panier, facettes)
     de re-scanner leur sous-arbre */
  window.AurumEngine = { refresh: observeAll };

  if (LEVEL === 0) {
    /* Animations coupées : on le signale au CSS (shimmer/glow, will-change)
       et tout devient visible immédiatement (compteurs à leur valeur finale) */
    body.classList.add('anim-off');
    observeAll();
  } else {
    /* Révélations d'abord, puis vérification immédiate pour les pages
       plus courtes que le viewport */
    try { observeAll(); bottomCheck(); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
    /* Particules différées hors du chemin critique : démarrage quand le
       navigateur est inactif (TBT mobile réduit), repli 200 ms. */
    var idleStart = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    idleStart(function () {
      try { initParticles(); } catch (e) { /* module décoratif — jamais bloquant */ }
    });
    try { initParallax(); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
    if (LEVEL === 2 && POINTER_FINE) {
      try { initTilt(); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
      if (body.dataset.animCursor === 'true') {
        try { initCursorTrail(); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
      }
    }
  }

  /* Éditeur de thème : purge des éléments retirés du DOM puis re-scan
     de la section rechargée (tous niveaux ; les init* ignorent les
     éléments déjà initialisés grâce aux drapeaux dataset) */
  document.addEventListener('shopify:section:load', function (event) {
    pruneParticles();
    pruneParallax();
    observeAll(event.target);
    if (LEVEL > 0) {
      try { initParticles(event.target); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
      try { initParallax(event.target); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
      if (LEVEL === 2 && POINTER_FINE) {
        try { initTilt(event.target); } catch (e) { /* ne jamais laisser un module décoratif casser le reste */ }
      }
    }
  });
})();
